/**
 * Cloud-Drucker-Station - Browser-basierter Bon-Druck
 * 
 * Diese Seite bleibt im Browser geöffnet und empfängt
 * Druckaufträge über Long-Polling - keine Installation nötig!
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Printer, Wifi, WifiOff, Volume2, VolumeX, Check, X,
  RefreshCw, Settings, Bell, AlertTriangle, Eye, EyeOff
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Druckvorlagen
const generateKundenBonHTML = (data) => {
  const items = data.items || [];
  const itemsHTML = items.map(item => `
    <tr>
      <td style="padding: 4px 0;">${item.quantity}x ${item.name}</td>
      <td style="text-align: right; padding: 4px 0;">${(item.price * item.quantity).toFixed(2)}€</td>
    </tr>
    ${item.options && item.options.length > 0 ? `
      <tr><td colspan="2" style="padding-left: 15px; font-size: 11px; color: #666;">
        → ${item.options.join(', ')}
      </td></tr>
    ` : ''}
  `).join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        @page { size: 80mm auto; margin: 0; }
        body { 
          font-family: 'Courier New', monospace; 
          font-size: 12px; 
          width: 76mm; 
          margin: 2mm;
          color: #000;
        }
        .header { text-align: center; margin-bottom: 10px; border-bottom: 1px dashed #000; padding-bottom: 10px; }
        .logo { font-size: 18px; font-weight: bold; }
        .order-num { font-size: 24px; font-weight: bold; margin: 10px 0; }
        .customer { background: #f5f5f5; padding: 8px; margin: 10px 0; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; }
        .total { border-top: 2px solid #000; margin-top: 10px; padding-top: 10px; font-weight: bold; font-size: 14px; }
        .footer { text-align: center; margin-top: 15px; font-size: 10px; color: #666; }
        .notes { background: #fff3cd; padding: 8px; margin: 10px 0; border-radius: 4px; font-size: 11px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">ZOZO BURGER</div>
        <div style="font-size: 10px;">Ihr Burger-Erlebnis</div>
      </div>
      
      <div style="text-align: center;">
        <div class="order-num">#${data.order_number}</div>
        <div>${data.order_type || 'Bestellung'}</div>
        <div style="font-size: 10px; color: #666;">${new Date(data.created_at || Date.now()).toLocaleString('de-DE')}</div>
      </div>
      
      ${data.customer_name ? `
        <div class="customer">
          <strong>${data.customer_name}</strong><br>
          ${data.customer_address ? `
            ${data.customer_address.street || ''}<br>
            ${data.customer_address.zip || ''} ${data.customer_address.city || ''}
          ` : ''}
        </div>
      ` : ''}
      
      <table>
        <tbody>
          ${itemsHTML}
        </tbody>
      </table>
      
      <div class="total">
        <table>
          <tr>
            <td>Zwischensumme:</td>
            <td style="text-align: right;">${(data.subtotal || 0).toFixed(2)}€</td>
          </tr>
          ${data.delivery_fee ? `
            <tr>
              <td>Liefergebühr:</td>
              <td style="text-align: right;">${data.delivery_fee.toFixed(2)}€</td>
            </tr>
          ` : ''}
          <tr style="font-size: 16px;">
            <td><strong>GESAMT:</strong></td>
            <td style="text-align: right;"><strong>${(data.total || 0).toFixed(2)}€</strong></td>
          </tr>
          <tr>
            <td>Bezahlung:</td>
            <td style="text-align: right;">${data.payment_method || 'Bar'}</td>
          </tr>
        </table>
      </div>
      
      ${data.notes ? `
        <div class="notes">
          <strong>📝 Hinweis:</strong> ${data.notes}
        </div>
      ` : ''}
      
      <div class="footer">
        <p>Vielen Dank für Ihre Bestellung!</p>
        <p>www.zozo-burger.de</p>
      </div>
    </body>
    </html>
  `;
};

const generateKuechenBonHTML = (data) => {
  const items = data.items || [];
  const itemsHTML = items.map(item => `
    <div style="margin: 8px 0; padding: 8px; background: #fff; border-left: 4px solid #e53935;">
      <div style="font-size: 18px; font-weight: bold;">${item.quantity}x ${item.name}</div>
      ${item.options && item.options.length > 0 ? `
        <div style="margin-top: 4px; color: #e53935; font-weight: bold;">
          ⚠️ ${item.options.join(' | ')}
        </div>
      ` : ''}
    </div>
  `).join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        @page { size: 80mm auto; margin: 0; }
        body { 
          font-family: Arial, sans-serif; 
          font-size: 14px; 
          width: 76mm; 
          margin: 2mm;
          background: #f5f5f5;
        }
        .header { 
          background: #e53935; 
          color: white; 
          padding: 10px; 
          text-align: center; 
          margin: -2mm -2mm 10px -2mm;
        }
        .order-num { font-size: 28px; font-weight: bold; }
        .type { font-size: 16px; text-transform: uppercase; }
        .time { font-size: 20px; font-weight: bold; margin: 10px 0; text-align: center; }
        .notes { background: #fff3cd; padding: 10px; margin: 10px 0; font-weight: bold; }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="type">🍳 KÜCHE</div>
        <div class="order-num">#${data.order_number}</div>
        <div>${data.order_type || 'Bestellung'} - ${data.customer_name || ''}</div>
      </div>
      
      <div class="time">
        ⏰ ${new Date(data.created_at || Date.now()).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
      </div>
      
      ${itemsHTML}
      
      ${data.notes ? `
        <div class="notes">
          📝 ${data.notes}
        </div>
      ` : ''}
    </body>
    </html>
  `;
};

export default function DruckerStation() {
  const [connected, setConnected] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem('cloud_print_token') || '');
  const [showToken, setShowToken] = useState(false);
  const [stationName, setStationName] = useState(() => localStorage.getItem('cloud_print_station') || 'Drucker-Station 1');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoPrint, setAutoPrint] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [printJobs, setPrintJobs] = useState([]);
  const [stats, setStats] = useState({ printed: 0, failed: 0 });
  const [polling, setPolling] = useState(false);
  const [lastPoll, setLastPoll] = useState(null);
  
  const printFrameRef = useRef(null);
  const audioRef = useRef(null);
  const pollingRef = useRef(false);

  // Sound abspielen
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Drucken
  const printJob = useCallback(async (job) => {
    return new Promise((resolve) => {
      try {
        let html;
        if (job.content_type === 'kitchen_ticket') {
          html = generateKuechenBonHTML(job.data);
        } else {
          html = generateKundenBonHTML(job.data);
        }

        const printFrame = printFrameRef.current;
        if (!printFrame) {
          resolve(false);
          return;
        }

        const frameDoc = printFrame.contentWindow || printFrame.contentDocument;
        const doc = frameDoc.document || frameDoc;
        
        doc.open();
        doc.write(html);
        doc.close();

        // Warten bis geladen, dann drucken
        setTimeout(() => {
          try {
            printFrame.contentWindow.print();
            resolve(true);
          } catch (e) {
            console.error('Print error:', e);
            resolve(false);
          }
        }, 500);
      } catch (e) {
        console.error('Print preparation error:', e);
        resolve(false);
      }
    });
  }, []);

  // Job abschließen
  const completeJob = useCallback(async (jobId, success, error = null) => {
    try {
      await fetch(`${BACKEND_URL}/api/cloud-print/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId, success, error, token })
      });
    } catch (e) {
      console.error('Complete job error:', e);
    }
  }, [token]);

  // Long-Polling für Jobs
  const pollForJobs = useCallback(async () => {
    if (!token || pollingRef.current) return;
    
    pollingRef.current = true;
    setPolling(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/cloud-print/poll?token=${encodeURIComponent(token)}&timeout=25`,
        { method: 'GET' }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          setConnected(false);
          toast.error('Token ungültig!');
          pollingRef.current = false;
          setPolling(false);
          return;
        }
        throw new Error('Poll failed');
      }
      
      setConnected(true);
      setLastPoll(new Date());
      
      const data = await response.json();
      
      if (data.jobs && data.jobs.length > 0) {
        console.log('📥 Jobs empfangen:', data.jobs.length);
        playNotificationSound();
        
        for (const job of data.jobs) {
          // Job zur Liste hinzufügen
          const newJob = {
            ...job,
            received_at: new Date(),
            status: 'received'
          };
          setPrintJobs(prev => [newJob, ...prev].slice(0, 50));
          
          if (autoPrint) {
            // Automatisch drucken
            const success = await printJob(job);
            
            // Status aktualisieren
            setPrintJobs(prev => prev.map(j => 
              j.job_id === job.job_id 
                ? { ...j, status: success ? 'printed' : 'failed' }
                : j
            ));
            
            setStats(prev => ({
              printed: prev.printed + (success ? 1 : 0),
              failed: prev.failed + (success ? 0 : 1)
            }));
            
            // Server benachrichtigen
            await completeJob(job.job_id, success);
            
            if (success) {
              toast.success(`Bon #${job.data?.order_number || job.job_id.slice(0, 8)} gedruckt!`);
            } else {
              toast.error('Druckfehler!');
            }
          } else {
            toast.info('Neuer Druckauftrag eingegangen');
          }
        }
      }
      
    } catch (e) {
      console.error('Poll error:', e);
      // Bei Fehler kurz warten
      await new Promise(r => setTimeout(r, 2000));
    } finally {
      pollingRef.current = false;
      setPolling(false);
      
      // Nächsten Poll starten
      if (token) {
        setTimeout(pollForJobs, 100);
      }
    }
  }, [token, autoPrint, playNotificationSound, printJob, completeJob]);

  // Polling starten wenn Token vorhanden
  useEffect(() => {
    if (token) {
      pollForJobs();
    }
    
    return () => {
      pollingRef.current = false;
    };
  }, [token, pollForJobs]);

  // Token speichern
  const saveToken = () => {
    localStorage.setItem('cloud_print_token', token);
    localStorage.setItem('cloud_print_station', stationName);
    toast.success('Einstellungen gespeichert!');
    setShowSettings(false);
    
    // Polling neu starten
    if (token) {
      pollForJobs();
    }
  };

  // Testdruck senden
  const sendTestPrint = async (role) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cloud-print/test-print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, printer_role: role })
      });
      
      if (response.ok) {
        toast.success('Testdruck gesendet!');
      } else {
        toast.error('Fehler beim Senden');
      }
    } catch (e) {
      toast.error('Fehler: ' + e.message);
    }
  };

  // Manuell drucken
  const handleManualPrint = async (job) => {
    const success = await printJob(job);
    
    setPrintJobs(prev => prev.map(j => 
      j.job_id === job.job_id 
        ? { ...j, status: success ? 'printed' : 'failed' }
        : j
    ));
    
    await completeJob(job.job_id, success);
    
    if (success) {
      toast.success('Gedruckt!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      {/* Hidden print frame */}
      <iframe
        ref={printFrameRef}
        style={{ display: 'none' }}
        title="print-frame"
      />
      
      {/* Notification sound */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+fn5+fn56enp2dnZ2dnZ2cnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJycnJw==" />

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  connected 
                    ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/40'
                    : 'bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/40'
                }`}>
                  {connected ? (
                    <Printer className="w-7 h-7 text-emerald-400" />
                  ) : (
                    <WifiOff className="w-7 h-7 text-red-400" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-white text-xl">
                    🖨️ Cloud-Drucker-Station
                  </CardTitle>
                  <p className="text-slate-400 text-sm">
                    {connected ? (
                      <span className="text-emerald-400 flex items-center gap-1">
                        <Wifi className="w-4 h-4" /> Verbunden - {polling ? 'Warte auf Bestellungen...' : 'Bereit'}
                      </span>
                    ) : (
                      <span className="text-red-400">Nicht verbunden - Token eingeben</span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="text-slate-400 hover:text-white"
                >
                  {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSettings(!showSettings)}
                  className={`text-slate-400 hover:text-white ${showSettings ? 'bg-slate-700' : ''}`}
                >
                  <Settings className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          {/* Statistiken */}
          <CardContent className="pt-0">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">{printJobs.length}</div>
                <div className="text-xs text-slate-400">Empfangen</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-emerald-400">{stats.printed}</div>
                <div className="text-xs text-slate-400">Gedruckt</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-red-400">{stats.failed}</div>
                <div className="text-xs text-slate-400">Fehler</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Einstellungen */}
        {showSettings && (
          <Card className="bg-slate-800/90 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">⚙️ Einstellungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">Token</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type={showToken ? 'text' : 'password'}
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Ihren Print-Token eingeben..."
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowToken(!showToken)}
                    className="text-slate-400"
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Token: Z2jipGMnmtX71RZ6k8iMNk_WMCEMiOOA4hE27sT-YyM
                </p>
              </div>
              <div>
                <Label className="text-slate-300">Stations-Name</Label>
                <Input
                  value={stationName}
                  onChange={(e) => setStationName(e.target.value)}
                  placeholder="z.B. Kasse 1, Küche..."
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Automatisch drucken</Label>
                <Switch
                  checked={autoPrint}
                  onCheckedChange={setAutoPrint}
                />
              </div>
              <Button onClick={saveToken} className="w-full bg-emerald-600 hover:bg-emerald-500">
                💾 Speichern & Verbinden
              </Button>
              
              {/* Testdruck Buttons */}
              {connected && (
                <div className="pt-4 border-t border-slate-700">
                  <Label className="text-slate-300 block mb-2">Testdruck senden:</Label>
                  <div className="flex gap-2">
                    <Button onClick={() => sendTestPrint('customer')} variant="outline" className="flex-1 border-slate-600">
                      🧾 Kundenbon
                    </Button>
                    <Button onClick={() => sendTestPrint('kitchen')} variant="outline" className="flex-1 border-slate-600">
                      🍳 Küchenbon
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Keine Verbindung */}
        {!connected && !showSettings && (
          <Card className="bg-slate-800/90 border-slate-700">
            <CardContent className="py-12 text-center">
              <WifiOff className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Nicht verbunden</h3>
              <p className="text-slate-400 mb-6">
                Bitte geben Sie Ihren Token in den Einstellungen ein.
              </p>
              <Button onClick={() => setShowSettings(true)} className="bg-purple-600 hover:bg-purple-500">
                <Settings className="w-4 h-4 mr-2" />
                Einstellungen öffnen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Warten auf Bestellungen */}
        {connected && printJobs.length === 0 && !showSettings && (
          <Card className="bg-slate-800/90 border-slate-700">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Bell className="w-10 h-10 text-emerald-400 animate-pulse" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">✅ Bereit für Bestellungen</h3>
              <p className="text-slate-400 mb-4">
                Eingehende Bestellungen werden hier angezeigt und automatisch gedruckt.
              </p>
              <p className="text-xs text-slate-500">
                Letzte Abfrage: {lastPoll ? lastPoll.toLocaleTimeString('de-DE') : '-'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Druckaufträge */}
        {printJobs.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-white">📋 Letzte Druckaufträge</h3>
            {printJobs.map((job) => (
              <Card 
                key={job.job_id} 
                className={`border transition-all ${
                  job.status === 'printed' 
                    ? 'bg-emerald-900/20 border-emerald-500/30'
                    : job.status === 'failed'
                    ? 'bg-red-900/20 border-red-500/30'
                    : 'bg-yellow-900/20 border-yellow-500/30'
                }`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                        job.content_type === 'kitchen_ticket'
                          ? 'bg-orange-500/20 border border-orange-500/30'
                          : 'bg-blue-500/20 border border-blue-500/30'
                      }`}>
                        {job.content_type === 'kitchen_ticket' ? '🍳' : '🧾'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold text-lg">
                            #{job.data?.order_number || job.job_id.slice(0, 8)}
                          </span>
                          <Badge className={
                            job.status === 'printed' 
                              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                              : job.status === 'failed'
                              ? 'bg-red-500/20 text-red-300 border-red-500/30'
                              : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                          }>
                            {job.status === 'printed' ? '✅ Gedruckt' : job.status === 'failed' ? '❌ Fehler' : '⏳ Warte...'}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-400">
                          {job.data?.customer_name || 'Kunde'} • {job.data?.order_type || 'Bestellung'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {job.status !== 'printed' && (
                        <Button
                          size="sm"
                          onClick={() => handleManualPrint(job)}
                          className="bg-purple-600 hover:bg-purple-500"
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          Drucken
                        </Button>
                      )}
                      {job.status === 'printed' && (
                        <Check className="w-6 h-6 text-emerald-400" />
                      )}
                      {job.status === 'failed' && (
                        <AlertTriangle className="w-6 h-6 text-red-400" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm space-y-1">
          <p>💡 <strong>Lassen Sie dieses Browser-Tab geöffnet</strong> um Bestellungen zu empfangen.</p>
          <p>Keine Software-Installation nötig - funktioniert direkt im Browser!</p>
        </div>
      </div>
    </div>
  );
}
